import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { FolderOpen, Search, X, Check, ChevronRight } from 'lucide-react'
import { useLivePractices } from '../hooks/useSupabaseLiveQuery'
import { useT } from '../i18n/useT'

// Picker per selezionare una Pratica da collegare a un task.
// Trigger compatto stile DateTimePicker; cliccando si apre un bottom-sheet
// con campo di ricerca + lista alfabetica delle pratiche aperte.
// Riusa le classi dtp-overlay / dtp-sheet / dtp-handle.
//
// Prop `renderTrigger` opzionale: render-fn `({ open, selected }) => ReactNode`
// che permette al chiamante di sostituire completamente il trigger
// (es. per integrarlo in un gruppo `field-rows`). Se assente, fallback al
// trigger di default `.pp-trigger`.
export default function PracticePicker({ value, onChange, renderTrigger }) {
  const t = useT()
  const practices = useLivePractices() ?? []
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')

  // Lista base: solo pratiche aperte, ordine alfabetico.
  const openPractices = useMemo(
    () => practices
      .filter(p => p.status === 'open')
      .sort((a, b) => a.title.localeCompare(b.title)),
    [practices]
  )

  // Se il task è già linkato a una pratica completata/archiviata, mantienila
  // visibile in cima così l'utente può scollegarla senza riaprirla.
  const candidates = useMemo(() => {
    if (!value) return openPractices
    if (openPractices.find(p => p.id === value)) return openPractices
    const linked = practices.find(p => p.id === value)
    return linked ? [linked, ...openPractices] : openPractices
  }, [openPractices, practices, value])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return candidates
    return candidates.filter(p => p.title.toLowerCase().includes(q))
  }, [candidates, search])

  const selected = value ? practices.find(p => p.id === value) : null

  const closeSheet = () => { setOpen(false); setSearch('') }
  const handlePick = (id) => { onChange(id); closeSheet() }

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ open: () => setOpen(true), selected })
      ) : (
        <button
          type="button"
          className={`pp-trigger${selected ? ' has-value' : ''}`}
          onClick={() => setOpen(true)}
        >
          <FolderOpen size={15} className="pp-trigger-icon" />
          <span className="pp-trigger-label">
            {selected ? selected.title : t('practice_select_none')}
          </span>
          <ChevronRight size={14} className="pp-trigger-chevron" />
        </button>
      )}

      {open && createPortal(
        <div className="dtp-overlay" onClick={closeSheet}>
          <div className="dtp-sheet pp-sheet animate-slideUp" onClick={e => e.stopPropagation()}>
            <div className="dtp-handle" />
            <div className="pp-header">
              <h3 className="pp-title">{t('practice_select_label')}</h3>
              <button type="button" className="btn-icon" onClick={closeSheet} aria-label={t('close')}>
                <X size={18} />
              </button>
            </div>

            <div className="pp-search">
              <Search size={15} className="pp-search-icon" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('practice_search_placeholder')}
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  className="pp-search-clear"
                  onClick={() => setSearch('')}
                  aria-label={t('close')}
                >
                  <X size={13} />
                </button>
              )}
            </div>

            <div className="pp-list">
              {/* Opzione "Nessuna" sempre in cima */}
              <button
                type="button"
                className={`pp-opt pp-opt--none${!value ? ' active' : ''}`}
                onClick={() => handlePick(null)}
              >
                <span className="pp-opt-label">{t('practice_select_none')}</span>
                {!value && <Check size={16} className="pp-opt-check" />}
              </button>

              {filtered.length === 0 ? (
                <div className="pp-empty">
                  {search ? t('practice_search_empty') : t('practices_empty_title')}
                </div>
              ) : (
                filtered.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    className={`pp-opt${value === p.id ? ' active' : ''}`}
                    onClick={() => handlePick(p.id)}
                  >
                    <span className="pp-opt-icon"><FolderOpen size={14} /></span>
                    <span className="pp-opt-label">{p.title}</span>
                    {value === p.id && <Check size={16} className="pp-opt-check" />}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
